'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useState } from 'react';
import { Anchor } from '@repo/ui/src/components/Anchor';
import ProductPrice from '@components/Product/Price';
import Product from '@models/product';
import CallToActionButton from '@components/Product/CallToActionButton';
import { logProduct } from '@utils/productWrapper';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Dropdown from './_Dropdown';
import useAddToBasket from '@hooks/basket/useAddToBasket';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ImageProps } from '@repo/ui/src/types';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import DropdownWrapper from '@components/ProductComparison/_DropdownWrapper';
import Image from '@repo/ui/src/components/Image/New';

interface Props {
  selectedSkus: (string | '')[];
  products: Product[];
  _testID: string;
  updateSelectedProducts: (sku: string, index: number) => void;
  noSelectionImage?: ImageProps;
  asContentRow?: boolean;
}

const ProductActions = ({
  selectedSkus,
  products,
  updateSelectedProducts,
  _testID,
  noSelectionImage,
  asContentRow,
}: Props) => {
  const [processingSkus, setProcessingSkus] = useState<string[]>([]);
  const { t } = useTranslation();
  const { locale } = useCurrentUrl();
  const { trigger } = useAddToBasket();
  const placeholderImage = buildImageSrc(noSelectionImage, 200, 1, 1);
  const mdAndUp = useIsMD();

  return (
    <>
      <DropdownWrapper
        products={products}
        selectedSkus={selectedSkus}
        asContentRow={asContentRow}
        updateSelectedProducts={updateSelectedProducts}
      />
      <div className="tw-relative tw-z-20" data-test-id={`product_actions_${_testID}`}>
        <div className="tw-relative tw-w-full">
          <div
            className={classNames(
              'tw-grid tw-bg-white tw-px-6 tw-pt-2 tw-gap-6 tw-pb-10 tw-w-full',
              asContentRow
                ? 'tw-grid-cols-[repeat(3,250px)] md:tw-grid-cols-4'
                : 'tw-grid-cols-2 md:tw-grid-cols-4',
            )}
          >
            {mdAndUp && <div></div>}
            {selectedSkus.map((sku, i) => {
              const product = products.find(
                ({ sku: productSku }: { sku: string }) => sku === productSku,
              );
              return (
                <div className="tw-flex tw-flex-col tw-text-center tw-h-full" key={sku}>
                  {asContentRow && mdAndUp && (
                    <h4 className="tra-text-web-heading-6">{product?.title}</h4>
                  )}
                  <div className="tw-relative">
                    <div className="md:tw-p-5 tw-flex tw-center" key={i}>
                      {product?.mainImageUrl ? (
                        <Image
                          className="tw-w-full tw-h-full tw-object-contain tw-aspect-square tw-max-h-[280px]"
                          src={product.mainImageUrl}
                          alt={product.title}
                          width={200}
                          height={200}
                        />
                      ) : (
                        <img
                          className="tw-w-full tw-h-full tw-object-contain tw-aspect-square tw-max-h-[280px]"
                          src={placeholderImage}
                          alt={noSelectionImage?.altTag}
                        />
                      )}
                    </div>
                    <Dropdown
                      sku={sku}
                      products={products}
                      updateSelectedProducts={updateSelectedProducts}
                      index={i}
                      selectedSkus={selectedSkus}
                      asContentRow={asContentRow}
                      className={classNames(asContentRow ? 'tw-hidden' : 'tw-block md:tw-hidden')}
                    />
                    {!!product?.price && (
                      <ProductPrice
                        product={product}
                        align="center"
                        variant="sub"
                        flipOrder
                        className="tw-pb-3 tw-items-center"
                      />
                    )}
                    <div>
                      <div className="tw-flex tw-flex-wrap tw-justify-center tw-items-center tw-gap-5">
                        {!asContentRow && product && (
                          <CallToActionButton
                            buttonProps={{
                              type: 'button',
                              size: 'small',
                            }}
                            className="!tw-px-2 xl:!tw-px-6"
                            onClickAddToCart={async () => {
                              setProcessingSkus([...processingSkus, sku]);
                              product.quantity = 1;
                              await trigger([product]);
                              setProcessingSkus(
                                processingSkus.filter((processingSku) => processingSku !== sku),
                              );
                            }}
                            buttonProcessing={processingSkus.includes(sku)}
                            processingText={t('genericContent.loadingLabel', undefined, {
                              default: 'loading...',
                            })}
                            product={product}
                          />
                        )}
                        {product && (
                          <Button
                            as={Anchor}
                            link={product?.productUrl}
                            className="!tw-no-underline !tw-px-2 xl:!tw-px-6"
                            size="small"
                            variant={asContentRow ? ButtonVariant.skewer : ButtonVariant.secondary}
                            onClick={() => {
                              logProduct('PRODUCT_CLICKED', {
                                product: product,
                                list_name: 'Grill Comparison',
                                locale,
                                position: i,
                                coupon_name: product?.promotionId,
                              });
                            }}
                          >
                            {t('productComparisonContent.viewDetailsLabel', undefined, {
                              default: 'Shop Now',
                            })}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {!mdAndUp && asContentRow && (
                    <h4 className="tra-text-web-heading-4 !tw-text-[32px]">{product?.title}</h4>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductActions;
